import { FC, useMemo, type JSX } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { usePermissions } from '@/hooks/usePermissions';
import { useTranslation } from '@/i18n';
import EntityTemplatesControl from '@/ui/components/entityTemplatesControl/EntityTemplatesControl';
import { DocumentTemplatePermissions } from '@payaca/permissions/documentTemplate/documentTemplate.permissions';
import { TemplatesPermissions } from '@payaca/permissions/templates/templates.permissions';
import { Helmet } from 'react-helmet';
import TabbedContentPageWrapper from '../pageWrappers/tabbedContentPageWrapper/TabbedContentPageWrapper';
import { DocumentTemplatesList } from './DocumentTemplatesList';

const ListedTemplatesPage: FC = (): JSX.Element => {
  const history = useHistory();
  const translate = useTranslation();

  const { userHasRequiredPermission } = usePermissions();

  const tabs = useMemo(() => {
    const t = [
      {
        content: <EntityTemplatesControl entityType="scheduledEvent" />,
        title: 'Event templates',
        label: 'event-templates',
        urlSlug: 'scheduledevent-templates',
      },
      {
        content: <EntityTemplatesControl entityType="task" />,
        title: 'Task templates',
        label: 'task-templates',
        urlSlug: 'task-templates',
      },
    ];

    if (
      userHasRequiredPermission([TemplatesPermissions.USE_PROPOSAL_TEMPLATES])
    ) {
      t.push({
        content: <EntityTemplatesControl entityType="proposal" />,
        title: 'Proposal templates',
        label: 'proposal-templates',
        urlSlug: 'proposal-templates',
      });
    }

    if (
      userHasRequiredPermission([
        DocumentTemplatePermissions.GET_DOCUMENT_TEMPLATES,
      ])
    ) {
      t.push({
        content: <DocumentTemplatesList />,
        title: 'Document templates',
        label: 'document-templates',
        urlSlug: 'document-templates',
      });
    }

    return t;
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'userHasRequiredPermission'
  }, []);

  const { tab } = useParams<{ tab?: string }>();
  const activeTabLabel = tabs.find((x) => x.urlSlug === tab)?.label;

  return (
    <>
      <Helmet title={translate('pages.templates.title')} />
      <TabbedContentPageWrapper
        className="listed-templates-page"
        tabbedContentConfig={{
          tabs: tabs,
          activeTabLabel: activeTabLabel || tabs[0].label,
          onChangeTab: (label: string) => {
            const newTabSlug = tabs.find((x) => x.label === label)?.urlSlug;
            if (newTabSlug) {
              history.push(`/templates/${newTabSlug}`);
            }
          },
        }}
      />
    </>
  );
};

export default ListedTemplatesPage;
